/**
 * The session page's audio trace: every media event of the session player, every swap, every
 * machine action and page lifecycle change, shipped in batches to POST /api/audio/trace, which logs
 * them as `[AudioTrace]` lines beside the server's own `[AudioStream]` ones.
 *
 * Opened 2026-10-06 for a stop at the end of the stream that happened on every meditation of the
 * operator's device and that neither the server logs nor GA4 could place.
 */

export type TraceData = Record<string, string | number | boolean | null | undefined>

interface Entry extends TraceData {
    seq: number
    at: number
    kind: string
}

const ENDPOINT = '/api/audio/trace'
const FLUSH_AFTER_MS = 3_000
/** Keeps a batch well under the server's body cap. */
const FLUSH_AT_ENTRIES = 40

const page = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().slice(0, 8) : String(Math.random()).slice(2, 10)
let seq = 0
let buffer: Entry[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let hooked = false

function post(beacon: boolean): void {
    if (timer) clearTimeout(timer)
    timer = null
    if (!buffer.length) return
    const body = JSON.stringify({page, entries: buffer})
    buffer = []
    try {
        if (beacon && navigator.sendBeacon(ENDPOINT, new Blob([body], {type: 'text/plain'}))) return
    } catch {
        // No beacon: fall through to fetch.
    }
    fetch(ENDPOINT, {method: 'POST', body, headers: {'Content-Type': 'text/plain'}, keepalive: true}).catch(() => {})
}

function hookPage(): void {
    if (hooked || typeof window === 'undefined') return
    hooked = true
    document.addEventListener('visibilitychange', () => {
        trace('page', {visibility: document.visibilityState})
        if (document.hidden) post(true)
    })
    window.addEventListener('pagehide', (e) => {
        trace('page', {pagehide: true, persisted: e.persisted})
        post(true)
    })
    window.addEventListener('pageshow', (e) => trace('page', {pageshow: true, persisted: e.persisted}))
    trace('page', {ua: navigator.userAgent, visibility: document.visibilityState})
}

export function trace(kind: string, data: TraceData = {}): void {
    if (typeof window === 'undefined') return
    hookPage()
    buffer.push({...data, seq: seq++, at: Date.now(), kind})
    if (buffer.length >= FLUSH_AT_ENTRIES) post(false)
    else if (!timer) timer = setTimeout(() => post(false), FLUSH_AFTER_MS)
}

/** The element's state in a few fields: what a stop is read from. */
export function mediaState(audio: HTMLAudioElement | null): TraceData {
    if (!audio) return {element: false}
    const {buffered} = audio
    return {
        t: Math.round(audio.currentTime * 100) / 100,
        dur: Number.isFinite(audio.duration) ? Math.round(audio.duration * 100) / 100 : String(audio.duration),
        buf: buffered.length ? Math.round(buffered.end(buffered.length - 1) * 100) / 100 : 0,
        ranges: buffered.length,
        rs: audio.readyState,
        ns: audio.networkState,
        paused: audio.paused,
        ended: audio.ended,
        err: audio.error?.code ?? null,
        errMsg: audio.error?.message || null,
        src: audio.currentSrc.split('/').slice(-2).join('/').slice(0, 60),
    }
}
